import {
  reactive,
  ref,
  computed,
  ComputedRef,
  Ref
} from 'vue'
import { format } from '@/utils/template'
import { CtOrder, EntryState } from '@/models/interfaces/content-type'
import { debounce } from 'lodash'

import { DATE_DISPLAY_FORMAT_SHORT_HOURS, ItemViewMode, STATUS } from '@/constants'
import useContentModule, { ListGroup } from './useContentModule'
import { Entry, RequestQueryItem, ServiceRequestConfig } from '@Protium/vue-ozone-utils'
import { date } from 'quasar'
import { $get, $groupBy, $reverse, isJSON, SortingDirection } from '@/utils'
import { errorToReadableString, addressIssuesToString } from '@/utils/error'
import { OrderType } from '@/models/types'
import Directr, { Timerange } from '@/services/directr'
import { Status } from '@/models/status'

export enum Group {
  Catalogue = 'catalogue',
  Category = 'category',
  sysId = 'sys.id',
  ID = 'id',
  Name = 'name'
}

interface ListConfig {
  group?: Group,
  sort?: string,
  order?: SortingDirection,
  reverse?: boolean,
  fields?: Array<string>
}

export const ORDER_STATUS_LIST = [
  STATUS.NEW,
  STATUS.PENDING,
  STATUS.QUEUED,
  STATUS.STARTING,
  STATUS.STARTED,
  STATUS.ACTIVE,
  STATUS.RESOLVED,
  STATUS.FINAL,
  STATUS.CANCELLED,
  STATUS.FAILED,
  STATUS.PAUSED,
  STATUS.UPDATED,
  STATUS.ARCHIVED
]

// "But thomas, why is there a list controller when a lot of this functionality also exists inside EntryList"
// Because we can control multiple lists and interact with our centralized store this way

export default function useList<T extends Entry> (type, $store, STATUS_LIST = ORDER_STATUS_LIST) {
  const {
    items: listItems,
    active,
    loading,
    selection,
    select,
    assert,
    model,
    set,
    setMulti,
    fetch,
    fetchTimerange,
    mutate,
    filterBy,
    stateGetterSetterRefs,
    setFilterFn,
    clear,
    requestHandler
  } = useContentModule<T>(type, $store)

  // @User Settings
  const settings = computed(() => $store.getters['User/Settings'])
  const groupBy = ref<Array<string> | string | ((item: T | Array<T>) => any)>('')
  const reverse = ref(false)
  const items = computed(() => {
    return applyGroupBy(groupBy.value, reverse.value === true ? $reverse(listItems.value) : listItems.value)
  })

  function applyGroupBy (path?: Array<string> | string | ((item: T | Array<T>) => any), payload?: Array<T>): Array<T> | Record<string, Array<T>> {
    if (!path) return payload || []

    if (payload) {
      const test = payload[0] && (typeof path === 'function' ? path(payload[0]) : $get(payload[0], path))
      if (test === undefined && typeof path !== 'function' && path.includes('.')) console.warn('The path provided to list.groupBy was a nested path containing ".", but the path resolved to undefined. Make sure the field you are reading is resolved, and not an unresolved UUID for example. Alternatively, pass a resolver function to groupBy as the path instead of a string')
      return $groupBy(payload, path)
    } else {
      groupBy.value = path
      return {}
    }
  }


  // const timerange = reactive({
  //   start: date.subtractFromDate(Date.now(), { months: 1 }),
  //   end: new Date()
  // })
  const DEFAULT_RANGE: { start: Date | null, end: Date | null } = {
    start: date.subtractFromDate(date.adjustDate(Date.now(), { hours: 0, minutes: 0, seconds: 0, milliseconds: 999 }), { days: 7 }),
    end: date.addToDate(date.adjustDate(Date.now(), { hours: 23, minutes: 59, seconds: 59, milliseconds: 999 }), { days: 1 })
  }
  const timerange = reactive({ ...DEFAULT_RANGE })

  const selectFields = ref<Array<string> | undefined>([])
  const query = ref<Array<RequestQueryItem>>([])
  const config = ref<ServiceRequestConfig>({})
  const viewMode = ref<ItemViewMode>(settings.value.orderViewMode || ItemViewMode.Default)

  const update = debounce((range) => {
    loading.value = true
    timerange.start = range.start
    timerange.end = range.end
    if (timerange && timerange.start && timerange.end) {
      void fetchTimerange(timerange, config.value, selectFields.value, query.value).then(() => loading.value = false)
    } else {
      void fetch(query.value, config.value).then(() => loading.value = false)
    }
  }, 1000, { leading: true })

  const init = (listConfig: ListConfig = {}, additionalQuery: Array<RequestQueryItem> = [], callback?: (items) => any) => (): Promise<any> => {
    return new Promise((resolve, reject) => {
      selectFields.value = listConfig.fields
      if (timerange && timerange.start && timerange.end) {
        fetchTimerange(timerange, config.value, selectFields.value, query.value.concat(additionalQuery || [])).then(res => {
          if (listConfig.group) groupBy.value = listConfig.group
          if (listConfig.sort) stateGetterSetterRefs.sortBy = listConfig.sort
          if (listConfig.order) stateGetterSetterRefs.sortOrder = listConfig.order
          if (typeof listConfig.reverse === 'boolean') reverse.value = listConfig.reverse
          resolve(callback ? callback(items) : items)
        }).catch(reject)
      } else {
        const fields = !selectFields.value ? [] : [{ select: selectFields.value }]
        fetch((query.value || []).concat(fields).concat(additionalQuery || []), config.value).then(res => {
          if (listConfig.group) groupBy.value = listConfig.group
          if (listConfig.sort) stateGetterSetterRefs.sortBy = listConfig.sort
          if (listConfig.order) stateGetterSetterRefs.sortOrder = listConfig.order
          if (typeof listConfig.reverse === 'boolean') reverse.value = listConfig.reverse
          resolve(callback ? callback(items) : items)
        }).catch(reject)
      }
    })
  }

  function trace (argument) {
    console.log('-- Trace --', argument)
    return assert.active(argument)
  }

  function getNotifications (order: CtOrder, field = 'status_info') {
    if (!order) {
      console.warn('getNotification missing order object')
      return []
    }
    const statusInfo = order[field] || {}
    return [
      { type: 'danger', value: 'Issue — ', label: errorToReadableString(statusInfo.failed) },
      // { type: 'warn', value: 'Address Issues — ', label: addressIssuesToString(order.shipping_address) },
      { type: 'neutral', value: 'Replacement — ', label: Array.isArray(statusInfo.replacement?.reason) ? statusInfo.replacement.reason.join(', ') : statusInfo.replacement?.reason },
      { type: 'neutral', value: 'Customer Note — ', label: order.notes },
      { type: 'neutral', value: 'Operational Note — ', label: order.operational_notes }
    ]
  }

  const actions = ref({})

  function setAction (name: string, fn) {
    actions.value[name] = fn
  }

  function getAction (name: string) {
    return actions.value[name]
  }
  function removeAction (name: string) {
    delete actions.value[name]
  }

  // NOTE the action callback is passed the order item, and the item actions (set/used in @setActions)
  function getActions<T extends Entry> (entry: T, item, range?: Array<number>) {
    const r = !range ? null : [...Array(range[1] - range[0] + 1).keys()].map((i: number) => i + range[0])
    return Object.keys(actions.value).filter((key, i) => !r ? true : r.includes(i)).map(action => actions.value[action](entry, item))
  }

  function resetActions () {
    actions.value = {}
  }

  // NOTE calls the items "setProp" with expand to set the item size
  function isActive (entry, expand?, force?: boolean) {
    // console.log('isactive', entry.reference, assert.active(force !== undefined ? force : entry))
    const active = assert.active(entry)
    if (typeof expand === 'function') expand(force !== undefined ? force : active)
    return force !== undefined ? force : active
  }

  function setActive (value) {
    active.value = value
  }

  function toggleItem (item, itemActions, e: Event) {
    if (isActive(item)) {
      if (e) e.stopPropagation()
      if (itemActions) {
        itemActions.setProp('expand', false)
        itemActions.setProp('active', false)
      }
      setActive(null)
    } else {
      if (e) e.stopPropagation()
      if (itemActions) {
        itemActions.setProp('expand', true)
        itemActions.setProp('active', true)
      }
      setActive(item)
    }
  }

  function computeFilters (filters: Array<(e: Entry, i: number, a: Array<Entry>) => Array<Entry> | ListGroup>) {
    return () => {
      // filters each group key
      if (typeof items.value === 'object' && !Array.isArray(items.value)) {
        // FIXME not sure how to allow for spread operand...
        const a: ListGroup = { ...items.value as Record<string, Array<Entry>> }
        Object.keys(a).forEach(group => {
          a[group] = filters.reduce((result, fn) => {
            return result.filter(fn).filter(f => !!f)
          }, a[group])
        })
        return a
      } else {
        // filters the array normally
        // COMBAK fix issue with entry type...
        const a: Array<Entry> = items.value
        return filters.reduce((result, fn) => {
          return result.filter(fn).filter(f => !!f)
        }, a)
      }
    }
  }

  // COMBAK should return only eligible status options based on input
  // for now just returns all statuses
  function getStatusOptions (order: CtOrder, scenario?: string, role?: string) {
    const status = order?.status
    if (!order || !status?.length) return STATUS_LIST

    //     const maxStatusIndex = status.reduce<number>((max, s) => (!STATUS.get(s)?.combinable && STATUS_LIST.indexOf(s) > max) ? STATUS_LIST.indexOf(s) : max, 0)
    // //    return maxStatusIndex > 2 ? STATUS_LIST.slice(maxStatusIndex) : STATUS_LIST

    //     let min = 0
    //     if (order.status_info?.picked) min = STATUS_LIST.indexOf(Status.Started)
    //     if (order.fulfillments?.length && maxStatusIndex >= STATUS_LIST.indexOf(Status.Resolved)) min = STATUS_LIST.indexOf(Status.Resolved)
    //     if (order.status_info?.cancelled?.cancelled?.length && order.products?.every(p => order.status_info?.cancelled?.cancelled?.includes(p.id))) min = STATUS_LIST.indexOf(Status.Cancelled)

    return Directr.StatusPattern.options(order.status).map(sp => sp.id)
  }

  // set order view mode
  function setViewMode (mode: number) {
    viewMode.value = mode
  }

  function setQuery (q = []) {
    query.value = q
  }

  function setRange (range?: Timerange) {
    if (range) {
      timerange.start = range.start === null ? null : new Date(range.start)
      timerange.end = range.end === null ? null : new Date(range.end)
    } else {
      timerange.start = DEFAULT_RANGE.start
      timerange.end = DEFAULT_RANGE.end
    }
  }

  function setConfig (c = {}) {
    config.value = c
  }

  const methods = {
    init,

    groupBy,
    reverse,

    sortBy: stateGetterSetterRefs.sortBy,
    filterBy,
    setFilterFn,
    setItems: set,
    setMulti,
    selectFields,
    trace,
    format,
    assert,
    fetch,
    mutate,
    update,
    select: select,
    clear,
    toggleItem,
    setQuery,
    setConfig,
    setRange,
    setActive,
    getAction,
    getActions,
    setAction,
    removeAction,
    resetActions,
    getStatusOptions,
    getNotifications,
    isActive,
    setViewMode,
    computeFilters,
    requestHandler
  }

  return {
    loading,
    items,
    total: listItems?.value ? listItems.value.length : 0,
    actions,
    viewMode,
    active,
    selection,
    model,
    timerange,
    request: {
      query,
      config
    },
    dateFormat: DATE_DISPLAY_FORMAT_SHORT_HOURS,
    // ...methods,
    useMethods: () => methods
  }
}
export type GenericList<T = any, R = any> = {
  loading: Ref<EntryState<T>['loading']>,
  items: ComputedRef<EntryState<T>['items'] | Record<string, EntryState<T>['items']>>,
  total: number,
  actions: Ref<Record<string, any>>,
  viewMode: Ref<ItemViewMode>,
  active: Ref<EntryState<T>['active']>,
  selection: Ref<EntryState<T>['selection']>,
  model: EntryState<T>['model'],
  timerange: { start: Date | string | typeof date, end: Date | string | typeof date },
  request: {
    query: Array<RequestQueryItem>,
    config: ServiceRequestConfig
  },
  dateFormat: string,

  useMethods (): {
    init (...args: Array<any>): any,
    groupBy: Ref<Array<string> | string | ((item: T) => any)>,
    reverse: Ref<boolean>, // TODO move out of useMethods
    sortBy (...args: Array<any>): any,
    filterBy (...args: Array<any>): any,
    setFilterFn (...args: Array<any>): any,
    setItems (...args: Array<any>): any,
    setMulti (...args: Array<any>): any,
    selectFields (...args: Array<any>): any,
    trace (...args: Array<any>): any,
    format (...args: Array<any>): any,
    assert (...args: Array<any>): any,
    fetch (...args: Array<any>): any,
    mutate (...args: Array<any>): any,
    update (...args: Array<any>): any,
    select (...args: Array<any>): any,
    clear (...args: Array<any>): any,
    toggleItem (...args: Array<any>): any,
    setQuery (...args: Array<any>): any,
    setConfig (...args: Array<any>): any,
    setRange (...args: Array<any>): any,
    setActive (...args: Array<any>): any,
    getAction (...args: Array<any>): any,
    getActions (...args: Array<any>): any,
    setAction (...args: Array<any>): any,
    removeAction (...args: Array<any>): any,
    resetActions (...args: Array<any>): any,
    getStatusOptions (...args: Array<any>): any,
    getNotifications (...args: Array<any>): any,
    isActive (...args: Array<any>): any,
    setViewMode (...args: Array<any>): any,
    computeFilters (...args: Array<any>): any,
    requestHandler: R
  }
}
